/**
 * The three settlement actions on one queue row, and the confirm step each one
 * needs before it moves money.
 *
 * Which actions exist is decided by the row's status, and by ABSENCE rather than
 * by a disabled button wherever the api's CAS makes the transition impossible:
 *
 * - **Pay** fires from `pending` and `failed` only. There is deliberately no Pay
 *   affordance on a `processing` row — the claim CAS accepts only
 *   `pending|failed`, so inviting a second attempt would be inviting a
 *   guaranteed failure. `failed` IS retryable; the enum's own description says
 *   so.
 * - **Cancel** fires from `pending` and `failed`. It takes NO reason argument,
 *   so this UI does not pretend to collect one.
 * - **Resolve** fires from `processing`, and only once the row is past the
 *   staleness cutoff. Its two outcomes are not symmetric: `paid` is allowed
 *   unconditionally, but `failed` is REFUSED by the api while a transfer
 *   exists — asserting failed there would strand real money. So it is treated as
 *   a server-validated outcome that can come back refused, never a local toggle.
 *
 * `ConfirmDialog` is message-only and cannot carry the text inputs resolve
 * needs, so these use `componentsv2/Dialog` + `Input`.
 *
 * Every confirm shows the SERVER's payee and amount as text. Neither is ever
 * editable here: both are frozen at creation, and the only screen where an
 * operator types an amount is the manual rail, where the row is being created.
 *
 * The buttons and the dialog are split (`RowActions` / `SettlementDialog`)
 * because the table renders every row twice — see `RowActions`.
 */
import { useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';

import { ExpertPaymentResolveOutcome, ExpertPaymentStatus } from '@/__generated__/graphql';
import Button from '@/componentsv2/Button';
import Dialog from '@/componentsv2/Dialog';
import Input from '@/componentsv2/Input';

import { isBlocked } from '../StatusChip';
import { shortfallFor } from '../balance';
import { type SettlementError, describeSettlementError } from '../errors';
import { formatMoney, resolveWait } from '../format';
import {
  CANCEL_EXPERT_PAYMENT,
  PAY_EXPERT_PAYMENT,
  RESOLVE_EXPERT_PAYMENT,
  STRIPE_BALANCE,
} from '../operations';
import type { QueuePayment } from './types';

export type SettlementActionKind = 'pay' | 'cancel' | 'resolve';

export interface PendingAction {
  payment: QueuePayment;
  kind: SettlementActionKind;
}

interface RowActionsProps {
  payment: QueuePayment;
  onAction: (action: PendingAction) => void;
}

/**
 * The buttons only — the dialog they open is rendered ONCE by the table.
 *
 * That split is not cosmetic. `PaymentTable` renders the desktop table and the
 * mobile card list simultaneously (CSS hides one), so anything held per row
 * exists twice. Two mounted copies of the same dialog means two modals opening
 * on one click, duplicated form field ids, and a second copy of a mutation
 * button for the same payment. Hoisting the dialog to the table makes exactly
 * one of it, for exactly one payment at a time.
 */
export default function RowActions({ payment, onAction }: RowActionsProps): JSX.Element | null {
  const canPay =
    payment.status === ExpertPaymentStatus.Pending || payment.status === ExpertPaymentStatus.Failed;
  const canCancel = canPay;
  const canResolve = payment.status === ExpertPaymentStatus.Processing;

  if (!canPay && !canCancel && !canResolve) return null;

  const blocked = isBlocked(payment);

  return (
    <div className="flex flex-wrap gap-8">
      {canPay ? (
        <Button size="small" onClick={() => onAction({ payment, kind: 'pay' })}>
          {blocked ? 'Retry payout' : 'Pay'}
        </Button>
      ) : null}
      {canCancel ? (
        <Button
          size="small"
          variant="secondary"
          onClick={() => onAction({ payment, kind: 'cancel' })}
        >
          Cancel
        </Button>
      ) : null}
      {canResolve ? (
        <Button
          size="small"
          variant="secondary"
          onClick={() => onAction({ payment, kind: 'resolve' })}
        >
          Resolve
        </Button>
      ) : null}
    </div>
  );
}

interface SettlementDialogProps {
  action: PendingAction | null;
  onDismiss: () => void;
  /** Re-read the queue; a settled row leaves its tab. */
  onSettled: () => void;
  /** Latched for the session once any Pay reports the freeze. */
  executionFrozen: boolean;
  onExecutionFrozen: () => void;
}

/**
 * The one dialog, rendered by the table for whichever action is pending.
 *
 * Keyed on the payment and action so switching rows remounts the body: a
 * half-typed resolve note must never carry over onto a different payment.
 */
export function SettlementDialog({
  action,
  onDismiss,
  onSettled,
  executionFrozen,
  onExecutionFrozen,
}: SettlementDialogProps): JSX.Element | null {
  if (!action) return null;

  const { payment, kind } = action;
  const done = () => {
    onDismiss();
    onSettled();
  };

  const title =
    kind === 'pay'
      ? isBlocked(payment)
        ? 'Retry this payout?'
        : 'Release this payment?'
      : kind === 'cancel'
        ? 'Cancel this payment?'
        : 'Record what actually happened';

  return (
    <Dialog open onClose={onDismiss} dialogTitle={title} showCloseButton disablePortal={false}>
      <div key={`${payment.id}-${kind}`}>
        {kind === 'pay' ? (
          <PayDialogBody
            payment={payment}
            executionFrozen={executionFrozen}
            onExecutionFrozen={onExecutionFrozen}
            onDone={done}
          />
        ) : kind === 'cancel' ? (
          <CancelDialogBody payment={payment} onDone={done} />
        ) : (
          <ResolveDialogBody payment={payment} onDone={done} />
        )}
      </div>
    </Dialog>
  );
}

/** Server-resolved payee and amount, rendered as text. Never an input. */
function FrozenFacts({ payment }: { payment: QueuePayment }): JSX.Element {
  return (
    <dl className="flex flex-col gap-8 rounded-md border border-surface-border bg-surface-elevated p-16">
      <div className="flex flex-col gap-2">
        <dt className="text-on-surface-muted util-caption">Payee</dt>
        <dd className="text-on-surface body-2">
          {payment.payeeIdentity?.name
            ? `${payment.payeeIdentity.name} (${payment.payee})`
            : payment.payee}
        </dd>
      </div>
      <div className="flex flex-col gap-2">
        <dt className="text-on-surface-muted util-caption">Amount</dt>
        <dd className="text-on-surface body-2">{formatMoney(payment.amount)}</dd>
      </div>
      <div className="flex flex-col gap-2">
        <dt className="text-on-surface-muted util-caption">Destination</dt>
        <dd className="text-on-surface body-2">
          {payment.rail === 'stripe_transfer'
            ? 'Stripe Connect transfer to the expert’s payout account'
            : 'Recorded off platform (wire or check)'}
        </dd>
      </div>
    </dl>
  );
}

function Notice({ error }: { error: SettlementError }): JSX.Element {
  const cls =
    error.tone === 'info'
      ? 'border-surface-border bg-surface-elevated text-on-surface'
      : error.tone === 'warning'
        ? 'border-yellow-700/40 bg-yellow-50 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-200'
        : 'border-red-700/40 bg-red-50 text-red-900 dark:bg-red-900/15 dark:text-red-200';
  return (
    <div className={`rounded-md border p-16 body-2 ${cls}`} role="status">
      {error.message}
    </div>
  );
}

function PayDialogBody({
  payment,
  executionFrozen,
  onExecutionFrozen,
  onDone,
}: {
  payment: QueuePayment;
  executionFrozen: boolean;
  onExecutionFrozen: () => void;
  onDone: () => void;
}): JSX.Element {
  const [error, setError] = useState<SettlementError | null>(null);
  const [pay, { loading }] = useMutation(PAY_EXPERT_PAYMENT);
  const blocked = isBlocked(payment);
  // `cache-first` against the entry BalancePreflight already populated: the
  // query takes no variables, so there is exactly one normalized
  // `ROOT_QUERY.stripeBalance` and this costs no round trip. Reading it here
  // rather than threading it down PaymentQueue -> PaymentTable -> dialog keeps
  // three components out of a prop they do not otherwise care about.
  const { data: balance } = useQuery(STRIPE_BALANCE, { fetchPolicy: 'cache-first' });
  const shortfall = shortfallFor(payment.amount, balance?.stripeBalance.available);

  async function go() {
    setError(null);
    try {
      await pay({ variables: { id: payment.id } });
      onDone();
    } catch (err) {
      const described = describeSettlementError(err, 'pay');
      setError(described);
      if (described.executionFrozen) onExecutionFrozen();
      // `not_claimable` here means the guard worked and the row is already
      // settled — informational, and the queue still needs re-reading. Close on
      // the caller's next tick so the operator reads the notice first.
      if (described.refetch) setTimeout(onDone, 2500);
    }
  }

  return (
    <div className="flex flex-col gap-16">
      {executionFrozen ? (
        <div className="rounded-md border border-yellow-700/40 bg-yellow-50 p-16 dark:bg-yellow-900/20">
          <p className="text-yellow-900 body-2 dark:text-yellow-200">
            Payment execution is frozen platform-wide right now. This will not go through until the
            freeze is lifted. Canceling and recording manual payments still work.
          </p>
        </div>
      ) : null}

      {blocked ? (
        <div className="rounded-md border border-yellow-700/40 bg-yellow-50 p-16 dark:bg-yellow-900/20">
          <p className="text-yellow-900 subtitle-1 dark:text-yellow-200">
            The last attempt was blocked: this expert’s Stripe payouts are not enabled.
          </p>
          <p className="mt-4 text-yellow-900 body-2 dark:text-yellow-200">
            Unless they have finished their payout setup since, this will be refused again before
            any transfer is created. Nothing is charged for a refused attempt.
          </p>
        </div>
      ) : null}

      {/* `role="alert"` because this is inserted after the dialog has already
          painted — the balance read resolves on its own clock — and nothing
          would otherwise announce it. It is also the only thing standing
          between a screen-reader operator and an irreversible transfer: the
          confirm button stays enabled by design, so the warning does all of
          the work. Assertive rather than the sibling `Notice`'s polite
          `role="status"` for that reason. Matches `ConnectGateBanner`, the
          repo's other gate-on-an-action banner. */}
      {shortfall ? (
        <div
          role="alert"
          className="rounded-md border border-yellow-700/40 bg-yellow-50 p-16 dark:bg-yellow-900/20"
        >
          <p className="text-yellow-900 subtitle-1 dark:text-yellow-200">
            The platform Stripe balance will not cover this payment.
          </p>
          <p className="mt-4 text-yellow-900 body-2 dark:text-yellow-200">
            {`Available in ${shortfall.available.currency}: ${formatMoney(shortfall.available)} — ` +
              `${formatMoney(payment.amount)} is ${formatMoney({
                cents: String(shortfall.shortfallCents),
                currency: payment.amount.currency,
              })} more than that. Stripe will decline the transfer, and a declined transfer is not ` +
              `retried automatically. Top the balance up first, or record it on the manual rail if ` +
              `it was paid off platform.`}
          </p>
        </div>
      ) : null}

      <FrozenFacts payment={payment} />

      <p className="text-on-surface-muted body-2">
        This moves money now, and there is no undo — the platform has no reversal or clawback path.
        Amount and payee are frozen and cannot be edited here.
      </p>

      {error ? <Notice error={error} /> : null}

      <Button onClick={() => void go()} loading={loading} disabled={loading}>
        {blocked ? 'Retry payout' : `Pay ${formatMoney(payment.amount)}`}
      </Button>
    </div>
  );
}

function CancelDialogBody({
  payment,
  onDone,
}: {
  payment: QueuePayment;
  onDone: () => void;
}): JSX.Element {
  const [error, setError] = useState<SettlementError | null>(null);
  const [cancel, { loading }] = useMutation(CANCEL_EXPERT_PAYMENT);

  async function go() {
    setError(null);
    try {
      await cancel({ variables: { id: payment.id } });
      onDone();
    } catch (err) {
      const described = describeSettlementError(err, 'cancel');
      setError(described);
      if (described.refetch) setTimeout(onDone, 2500);
    }
  }

  return (
    <div className="flex flex-col gap-16">
      <FrozenFacts payment={payment} />

      <p className="text-on-surface-muted body-2">
        Canceling voids the obligation — the expert will not be paid this. It is terminal: a
        canceled payment cannot be reopened, though a replacement can be accrued or recorded.
      </p>

      {payment.status === ExpertPaymentStatus.Failed && payment.rail === 'stripe_transfer' ? (
        <p className="text-on-surface-muted body-2">
          This row failed on the Stripe rail, so canceling first checks Stripe for a transfer that
          may exist anyway. If one is found the cancel is refused, and the right move is to release
          the payment again so the platform adopts that transfer as paid.
        </p>
      ) : null}

      {error ? <Notice error={error} /> : null}

      <Button variant="destructive" onClick={() => void go()} loading={loading} disabled={loading}>
        Cancel this payment
      </Button>
    </div>
  );
}

function ResolveDialogBody({
  payment,
  onDone,
}: {
  payment: QueuePayment;
  onDone: () => void;
}): JSX.Element {
  const [outcome, setOutcome] = useState<ExpertPaymentResolveOutcome>(
    ExpertPaymentResolveOutcome.Paid
  );
  const [providerRef, setProviderRef] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<SettlementError | null>(null);
  const [resolve, { loading }] = useMutation(RESOLVE_EXPERT_PAYMENT);

  const wait = resolveWait(payment.updatedAt);
  const assertingPaid = outcome === ExpertPaymentResolveOutcome.Paid;
  // providerRef is typed nullable at the schema but is required when asserting
  // paid — replicated here so the operator finds out before the round trip.
  const refMissing = assertingPaid && providerRef.trim().length === 0;
  const noteMissing = note.trim().length === 0;

  async function go() {
    setError(null);
    try {
      await resolve({
        variables: {
          id: payment.id,
          outcome,
          providerRef: assertingPaid ? providerRef.trim() : null,
          note: note.trim(),
        },
      });
      onDone();
    } catch (err) {
      const described = describeSettlementError(err, 'resolve');
      setError(described);
      if (described.refetch) setTimeout(onDone, 2500);
    }
  }

  if (wait) {
    return (
      <div className="flex flex-col gap-16">
        <FrozenFacts payment={payment} />
        <p className="text-on-surface body-2">
          {`Not yet — this payment was claimed less than ${wait} ago and its executor may still ` +
            'be mid-transfer. Forcing an outcome now could contradict a transfer that is about ' +
            'to land.'}
        </p>
        <p className="text-on-surface-muted body-2">
          The reconciler is already working on it: it looks stuck rows up against Stripe every hour
          and resolves them on its own. Come back in about {wait} if it has not.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-16">
      <FrozenFacts payment={payment} />

      <p className="text-on-surface-muted body-2">
        This payment was claimed for execution and its outcome was never recorded. Assert what
        actually happened — check Stripe first; this does not move money either way.
      </p>

      <fieldset className="flex flex-col gap-8">
        <legend className="text-on-surface-muted util-caption">Outcome</legend>
        <label className="flex items-start gap-8">
          <input
            type="radio"
            name={`outcome-${payment.id}`}
            className="mt-4"
            checked={assertingPaid}
            onChange={() => setOutcome(ExpertPaymentResolveOutcome.Paid)}
          />
          <span className="text-on-surface body-2">
            The money did move — mark it paid.
            <span className="block text-on-surface-muted">A transfer reference is required.</span>
          </span>
        </label>
        <label className="flex items-start gap-8">
          <input
            type="radio"
            name={`outcome-${payment.id}`}
            className="mt-4"
            checked={!assertingPaid}
            onChange={() => setOutcome(ExpertPaymentResolveOutcome.Failed)}
          />
          <span className="text-on-surface body-2">
            The money did not move — mark it failed.
            <span className="block text-on-surface-muted">
              Refused if a transfer exists for this payment. That refusal is the server protecting
              real money, not an error to work around.
            </span>
          </span>
        </label>
      </fieldset>

      {assertingPaid ? (
        <Input
          label="Transfer reference"
          required
          value={providerRef}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setProviderRef(e.target.value)}
          helperText="The Stripe transfer id, as evidence the money moved. Max 255 characters."
        />
      ) : null}

      <Input
        label="Note"
        required
        value={note}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
        helperText="What you checked and where. Appended to this payment's trail permanently."
      />

      {error ? <Notice error={error} /> : null}

      <Button
        onClick={() => void go()}
        loading={loading}
        disabled={loading || refMissing || noteMissing}
      >
        Record outcome
      </Button>
    </div>
  );
}
